import {FC, startTransition, useCallback, useContext, useMemo, useState} from 'react';
import {useRecoilState} from 'recoil';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MenuIcon from '@mui/icons-material/Menu';
import {AppBar, Avatar, Button, IconButton, Menu, MenuItem, Stack, Toolbar} from '@mui/material';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography';
import {Container} from '@mui/system';
import {ConfirmAction} from '../../modals/ConfirmAction';
import {ModalContainer} from '../../modals/ModalContainer';

import {useRightPath} from '../../../hooks/useRightPath';

import {authState} from '../../../store/auth/atoms';

import {AuthService} from '../../../api/services/auth.service';

import {location, subLocation} from '../../../shared/menu.data';

import {colors} from '../../../shared/colors';

import {EventBusContext} from '../../../providers/EventBus';
import {PROJECT_MANAGER_AND_ABOVE_ROLES} from '../../../shared/constants';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {ToastHelpers} from '../../../shared/helpers/toastHelpers';
import {useModalPageStore} from '../../../store/modalPage';
import {Notifications} from '../../../modules/notifications';
import styles from './Header.module.scss';

const Header: FC = () => {
  const [{user}, setAuthState] = useRecoilState(authState);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [openLogout, setOpenLogout] = useState(false);
  const {setModalKey} = useModalPageStore();
  const {bus} = useContext(EventBusContext);
  const open = Boolean(anchorElUser);
  const isProjectManagerOrAbove = checkAuthorities(PROJECT_MANAGER_AND_ABOVE_ROLES, user?.roles || []);

  const {validUrl: pathname, sublocationChecking} = useRightPath();

  const handleSettingsClick = () => {
    setAnchorElUser(null);
  };

  const settings = useMemo(
    () => [
      ...(isProjectManagerOrAbove
        ? [
            {
              label: 'Профиль',
              path: '/profile',
              onClick: () => {
                startTransition(() => setModalKey('profile'));
                handleSettingsClick();
              },
            },
            {
              label: 'Баланс и оплата',
              path: '/balance-payment',
              onClick: () => {
                startTransition(() => setModalKey('balancePayment'));
                handleSettingsClick();
              },
            },
          ]
        : []),
      {
        label: 'Выйти',
        loadable: true,
        onClick: () => {
          setOpenLogout(true);
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isProjectManagerOrAbove],
  );

  const closeLogout = useCallback(() => {
    setOpenLogout(false);
  }, []);

  const handleLogout = useCallback(async (setLoading: (bool: boolean) => void) => {
    try {
      setLoading(true);
      await AuthService.logout();
      sessionStorage.removeItem('current');
      setAuthState(() => ({
        user: null,
        isLoggedIn: false,
        isFirstLogin: false,
      }));
      ToastHelpers.setIsAdmin(false);
      setOpenLogout(false);
    } catch (error) {
      bus?.emit('error', [error]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuCLick = () => {
    if (document.body.getAttribute('data-menu') === 'open') {
      document.body.setAttribute('data-menu', 'closed');
      localStorage.setItem('menu', 'closed');
    } else {
      document.body.setAttribute('data-menu', 'open');
      localStorage.setItem('menu', 'open');
    }
  };

  return (
    <>
      <AppBar className={styles.header} position="fixed">
        <Container maxWidth="xl" sx={{maxWidth: '2048px !important', pl: '12px !important'}}>
          <Toolbar disableGutters sx={{justifyContent: 'space-between', width: '100%'}}>
            <Stack direction="row" spacing={2}>
              <IconButton onClick={handleMenuCLick}>
                <MenuIcon sx={{color: '#fff'}} fontSize="large" />
              </IconButton>
              <Stack direction="column" sx={{justifyContent: 'center'}}>
                <Typography sx={{fontWeight: 600}} variant="h5">
                  {sublocationChecking ? subLocation[sublocationChecking.path] : location[pathname]}
                </Typography>
              </Stack>
            </Stack>
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  background: '#fff',
                  borderRadius: '4px',
                  px: 1,
                  gap: 1,
                }}
              >
                <Button
                  sx={{
                    p: 0.5,
                    background: 'transparent',
                    borderColor: 'transparent',
                    '&:hover': {
                      background: colors['bg--primary'],
                      borderColor: 'transparent',
                    },
                  }}
                  onClick={handleOpenUserMenu}
                  endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  variant="outlined"
                >
                  <Avatar alt="Если будет" src="" />
                  <Typography sx={{textTransform: 'none', marginLeft: '1rem'}} variant="subtitle1">
                    {`${user?.firstName} ${user?.lastName}`}
                  </Typography>
                </Button>
                <Notifications roles={user?.roles || []} />
              </Stack>

              <Menu
                sx={{mt: '45px'}}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem key={setting.label} onClick={setting.onClick}>
                    <Typography textAlign="center">{setting.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {openLogout && (
        <ModalContainer
          sideEffect={handleLogout}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openLogout}
              handleClose={closeLogout}
              handleSideEffect={sideEffect}
              title="Подтвердите выход из системы"
              buttonTitle="Выйти"
            />
          )}
        />
      )}
    </>
  );
};

export default Header;
